import React, { useState, useCallback } from 'react';
import { Card } from '@playground/ui';
import { apiFetch } from '@/platform/api.js';

interface PlayerRow {
  playerId: string;
  displayName: string;
  ip: string;
  firstSeen: number;
  lastSeen: number;
  credits: number;
}

function fmt(ts: number): string {
  if (!ts) return '—';
  return new Date(ts).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' });
}

export function Players({ token }: { token: string }) {
  const [search, setSearch] = useState('');
  const [rows, setRows] = useState<PlayerRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [creditInputs, setCreditInputs] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState<Record<string, boolean>>({});
  const [saved, setSaved] = useState<Record<string, boolean>>({});
  const [creditError, setCreditError] = useState<Record<string, boolean>>({});

  const runSearch = useCallback(async (q: string) => {
    setLoading(true);
    setError('');
    try {
      const res = await apiFetch(`/api/admin/players?search=${encodeURIComponent(q)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Request failed');
      const data = await res.json() as { players: PlayerRow[] };
      setRows(data.players);
    } catch {
      setError('Failed to load players.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  function handleSearch(e: React.FormEvent) {
    e.preventDefault();
    void runSearch(search);
  }

  async function handleSetCredits(playerId: string) {
    const raw = creditInputs[playerId] ?? '';
    const amount = parseInt(raw, 10);
    if (isNaN(amount) || amount < 0) return;
    setSaving((s) => ({ ...s, [playerId]: true }));
    try {
      const res = await apiFetch(`/api/admin/players/${playerId}/credits`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ credits: amount }),
      });
      if (!res.ok) throw new Error('Failed');
      const data = await res.json() as { balance: number };
      setRows((prev) => prev.map((r) => r.playerId === playerId ? { ...r, credits: data.balance } : r));
      setSaved((s) => ({ ...s, [playerId]: true }));
      setTimeout(() => setSaved((s) => ({ ...s, [playerId]: false })), 2000);
    } catch {
      // A failed balance update used to leave no trace: the row kept its old
      // value and the admin assumed the change had gone through.
      setCreditError((e) => ({ ...e, [playerId]: true }));
      setTimeout(() => setCreditError((e) => ({ ...e, [playerId]: false })), 4000);
    } finally {
      setSaving((s) => ({ ...s, [playerId]: false }));
    }
  }

  return (
    <div>
      <form onSubmit={handleSearch} className="mb-4 flex gap-2">
        <label htmlFor="player-search" className="sr-only">Search players by name</label>
        <input
          id="player-search"
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name…"
          className="flex-1 rounded-lg border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm bg-white dark:bg-gray-800 dark:text-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
        />
        <button
          type="submit"
          className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 transition-colors"
        >
          Search
        </button>
      </form>

      {error && <p className="mb-3 text-sm text-red-600 dark:text-red-400">{error}</p>}

      {loading ? (
        <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-8">Loading…</p>
      ) : rows.length === 0 ? (
        <Card>
          <p className="text-center text-sm text-gray-500 dark:text-gray-400 py-6">
            {search ? 'No players found.' : 'Enter a name and press Search.'}
          </p>
        </Card>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-700 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                <th scope="col" className="pb-2 text-left">Name</th>
                <th scope="col" className="pb-2 text-left">IP</th>
                <th scope="col" className="pb-2 text-left">First seen</th>
                <th scope="col" className="pb-2 text-left">Last seen</th>
                <th scope="col" className="pb-2 text-right">Credits</th>
                <th scope="col" className="pb-2 text-right">Set credits</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.playerId} className="border-b border-gray-100 dark:border-gray-700 last:border-0">
                  <td className="py-2 font-medium text-gray-900 dark:text-gray-100">{row.displayName || <span className="text-gray-500 dark:text-gray-400 italic">—</span>}</td>
                  <td className="py-2 font-mono text-xs text-gray-500">{row.ip || '—'}</td>
                  <td className="py-2 text-gray-500">{fmt(row.firstSeen)}</td>
                  <td className="py-2 text-gray-500">{fmt(row.lastSeen)}</td>
                  <td className="py-2 text-right font-semibold tabular-nums">{row.credits.toLocaleString()}</td>
                  <td className="py-2 text-right">
                    <div className="flex items-center justify-end gap-1.5">
                      <input
                        type="number"
                        min="0"
                        value={creditInputs[row.playerId] ?? ''}
                        onChange={(e) => setCreditInputs((c) => ({ ...c, [row.playerId]: e.target.value }))}
                        placeholder={String(row.credits)}
                        aria-label={`Set credits for ${row.displayName || row.playerId}`}
                        aria-invalid={creditError[row.playerId] || undefined}
                        className="w-24 rounded border border-gray-300 dark:border-gray-600 px-2 py-1 text-xs bg-white dark:bg-gray-800 dark:text-gray-100 text-right tabular-nums focus:outline-none focus-visible:ring-1 focus-visible:ring-indigo-500"
                      />
                      <button
                        type="button"
                        onClick={() => void handleSetCredits(row.playerId)}
                        disabled={saving[row.playerId]}
                        aria-label={`Set credits for ${row.displayName || row.playerId}`}
                        className={[
                          'rounded px-2 py-1 text-xs font-semibold transition-colors disabled:opacity-50',
                          'focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500',
                          creditError[row.playerId]
                            ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                            : saved[row.playerId]
                              ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                              : 'bg-indigo-600 text-white hover:bg-indigo-700',
                        ].join(' ')}
                      >
                        {creditError[row.playerId] ? 'Failed' : saved[row.playerId] ? '✓' : 'Set'}
                      </button>
                    </div>
                    {creditError[row.playerId] && (
                      <p role="alert" className="mt-1 text-[11px] text-red-600 dark:text-red-400">
                        Could not update balance
                      </p>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
